import { jsPDF } from 'jspdf';
import { autoTable } from 'jspdf-autotable';
import { ManifestPdfReportContent, ManifestReportData, REPORT_TYPE_ORIENTATION } from '../ReportGeneratorTypes';
import { generateReportHeaderPdf } from './reportHeader';
import { getVoyageNumberString } from 'src/utils/stowage/stowageRotationUtils';

/**
 * Generates a PDF report showing the manifold plan details
 * @param doc - jsPDF document instance
 * @param data - Report data containing manifold plan information
 */

const generateData = (cargoInTanksList: ManifestPdfReportContent[]) => {
    const result = [];

    cargoInTanksList.forEach((cargoContent) => {
        const data = {
            cargoNumber: cargoContent.cargoNumber,
            tradeName: cargoContent.tradeName,
            operationType: cargoContent.operationType,
            shipFigure: cargoContent.shipFigure,
            billOfLadingFigure: cargoContent.billOfLadingFigure,
            litreWeight: cargoContent.litreWeight,
            flashPoint: cargoContent.flashPoint,
            heating: cargoContent.heating,
            un: cargoContent.un,
            imdg: cargoContent.imdg,
            marpol: cargoContent.marpol,
            uscg: cargoContent.uscg,
            loadPorts: cargoContent.loadPorts,
            unloadingPorts: cargoContent.unloadingPorts,
            stowedInTanks: cargoContent.stowedInTanks,
        };
        result.push(data);
    });

    return result.map((row: any) => [
        row.cargoNumber,
        row.tradeName,
        row.operationType,
        row.shipFigure,
        row.billOfLadingFigure,
        row.litreWeight,
        row.flashPoint,
        row.heating,
        row.un,
        row.imdg,
        row.marpol,
        row.uscg,
        row.loadPorts,
        row.unloadingPorts,
        row.stowedInTanks,
    ]);
};

const calculateTotals = (cargoInTanksList: ManifestPdfReportContent[]) => {
    let totalShipFigure = 0;
    let totalBillOfLadingFigure = 0;

    cargoInTanksList.forEach((cargoContent) => {
        // Parse as number and add to total, handling potential non-numeric values
        const shipFigure = parseFloat(cargoContent.shipFigure) || 0;
        const blFigure = parseFloat(cargoContent.billOfLadingFigure) || 0;

        totalShipFigure += shipFigure;
        totalBillOfLadingFigure += blFigure;
    });

    return {
        totalShipFigure: totalShipFigure.toFixed(2),
        totalBillOfLadingFigure: totalBillOfLadingFigure.toFixed(2)
    };
};

const addFooters = (doc, totals) => {
    const pageCount = doc.internal.getNumberOfPages()

    doc.setLineWidth(0.3);
    doc.line(10, 190, REPORT_TYPE_ORIENTATION.MANIFESTS === 'portrait' ? 210 : 287, 190);

    doc.setFont('helvetica', 'italic')
    doc.setFontSize(8)

    // Add totals on the last page
    doc.setPage(pageCount);
    doc.setFont('helvetica', 'bold');
    doc.text(`Total Ship Figure: ${totals.totalShipFigure} MT`, 10, 196);
    doc.text(`Total B/L Figure: ${totals.totalBillOfLadingFigure} MT`, 10, 200);

    // Add page numbers to all pages
    doc.setFont('helvetica', 'italic');
    for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i)
        doc.text('Page ' + String(i) + ' of ' + String(pageCount), doc.internal.pageSize.width / 2, 200, {
            align: 'center'
        })
    }
}

export function generateManifestsReport(doc: jsPDF, data: ManifestReportData, firstPage: boolean): void {
    if (!firstPage) {
        doc.addPage();
    }

    const fullVoyageNumber = getVoyageNumberString(data.selectedPortCall);

    let _titleText = `${data.headerTitle} ${fullVoyageNumber} ${data.selectedPortCall?.portDisplayName ?? ''}`;

    if (data.selectedBerthCall?.berthName) {
        _titleText += ` - ${data.selectedBerthCall.berthName}`;
    }

    generateReportHeaderPdf(doc, data, _titleText, REPORT_TYPE_ORIENTATION.MANIFESTS);

    const headers = [
        'Cargo #',
        'Trade name',
        'Type',
        'Ship Fig MT',
        'BL Fig MT',
        'Litre Weight',
        'Flash P.',
        'Heating',
        'UN #',
        'IMDG #',
        'Marpol',
        'USCG #',
        'Load port',
        'Unload port',
        'Stowed in',
    ];

    // Calculate totals
    const totals = calculateTotals(data.cargoInTanksList);

    autoTable(doc, {
        styles: {
            cellPadding: 0.8,
            fontSize: 7,
            lineColor: [73, 138, 159],
            lineWidth: 0.1
        },
        columnStyles: {
            3: { halign: 'right' }, 4: { halign: 'right' },
            5: { halign: 'right' }, 6: { halign: 'right' }, 7: { halign: 'right' },
            8: { halign: 'right' }, 9: { halign: 'right' }, 10: { halign: 'right' }, 11: { halign: 'right' },
        },
        margin: { top: 25, left: 10, right: 10 },
        head: [headers],
        body: generateData(data.cargoInTanksList),
        startY: 25,
    });

    // Pass totals to the addFooters function
    addFooters(doc, totals);
}



